import React, { useState, useEffect } from "react";
import { Card } from "../../../Card";
import { Loading } from "../../../Loader";
import { Filter } from "../../../Filter";
import DropdownButton from "../../../DropdownButton";
import Pagination from "../../../Pagination/Pagination";
import CardCount from "../../../CardCount/CardCount";
import moment from "moment";
import Axios from "axios";
import HOST_URL from "../../../../configs/api";
import toastMessage from "../../../toastMessage";
import "./styles.css";
import CreateLoanProductModal from "./CreateLoanProductModal";

const LoanProducts = ({ token, apps, user_id, role }) => {
  const [appid, setAppid] = useState(null);
  const [fromDate, setFromDate] = useState(moment().subtract("5", "years").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));
  const [showProductModal, setShowProductModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loanProducts, setLoanProducts] = useState([]);
  const [summary, setSummary] = useState({});
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [validationErrors, setValidationErrors] = useState({});

  // loan product creation/edit
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loanProductData, setLoanProductData] = useState({
    name: "",
    code: "",
    description: "",
    app_id: appid?.value,
    amortization_method: 'reducing_balance',
    interest_rate: 1.5,
    interest_frequency: 'monthly',
    duration: 12,
    duration_unit: 'months'
  });

  useEffect(() => {
    if (apps.length > 0) {
      setAppid(apps?.[0]);
      getData();
    }
  }, [apps?.length]);

  const getData = async () => {
    setIsLoading(true);
    //const { fromDate, toDate } = state;
    let params = {
      app_id: appid?.value || apps?.[0]?.value,
      //fromDate,
      //toDate,
    };

    if (role !== "admin") {
      params.user_id = user_id;
    }

    try {
      const response = await Axios({
        method: "GET",
        url: HOST_URL.url + "/loan-products",
        params,
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      const { data, summary } = response.data;
      setLoanProducts(data);
      setSummary({
        total_products: data?.length || 0,
        active_products: data.length || 0,
        inactive_products: data?.filter((product) => product?.status === 'inactive')?.length || 0,
      });
      setIsLoading(false);
    } catch (error) {
      toastMessage("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenProductModal = (product = null) => {
    setShowProductModal(true);
    if (product) {
      setSelectedProduct(product);
      setLoanProductData((prev) => ({
        ...prev,
        name: product.name,
        code: product.code,
        description: product.description,
        app_id: product.app_id,
        amortization_method: product.amortization_method,
        interest_rate: product.interest_rate,
        interest_frequency: product.interest_frequency,
        duration: product.duration,
        duration_unit: product.duration_unit,
      }));
    } else {
      setSelectedProduct(null);
      setLoanProductData((prev) => ({
        ...prev,
        name: "",
        code: "",
        description: "",
        app_id: appid?.value,
        amortization_method: 'reducing_balance',
        interest_rate: 1.5,
        interest_frequency: 'monthly',
        duration: 12,
        duration_unit: 'months',
      }));
    }
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
    setLoanProductData((prev) => ({
      ...prev,
      name: "",
      code: "",
      description: "",
      app_id: appid?.value,
      amortization_method: 'reducing_balance',
      interest_rate: 1.5,
      interest_frequency: 'monthly',
      duration: 12,
      duration_unit: 'months',
    }));
    setShowProductModal(false);
  };

  const validateLoanProductData = () => {
    const errors = {};
    if (!loanProductData.name) {
      errors.name = "Name is required";
    }
    if (!loanProductData.code) {
      errors.code = "Code is required";
    }
    if (!loanProductData.description) {
      errors.description = "Description is required";
    }
    if (!loanProductData.amortization_method) {
      errors.amortization_method = "Amortization method is required";
    }
    if (!loanProductData.interest_rate) {
      errors.interest_rate = "Interest rate is required";
    }
    if (!loanProductData.interest_frequency) {
      errors.interest_frequency = "Interest frequency is required";
    }
    if (!loanProductData.duration) {
      errors.duration = "Duration is required";
    }
    if (!loanProductData.duration_unit) { 
      errors.duration_unit = "Duration unit is required";
    }
    setValidationErrors(errors);

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateLoanProductData();

    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const method = selectedProduct ? "PATCH" : "POST";
      const url = selectedProduct 
        ? `${HOST_URL.url}/loan-products/${selectedProduct._id}`
        : `${HOST_URL.url}/loan-products`;

      const response = await Axios({
        method,
        url,
        data: {
          ...loanProductData,
        },
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      toastMessage("success", response.data.message);
      handleCloseModal();
      getData();
    } catch (error) {
      console.log(error);
      toastMessage("error",
        error?.response?.data?.error ||
        error?.message ||
        'Error creating/updating loan product');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this loan product?')) {
      return;
    }

    try {
      await Axios({
        method: "DELETE",
        url: `${HOST_URL.url}/loan-products/${productId}`,
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      toastMessage("success", "Loan product deleted successfully");
      getData();
    } catch (error) {
      toastMessage("error", error?.response?.data?.error || 'Error deleting loan product');
    }
  };

  const getProductActions = (product) => {
    return [
      {
        text: 'Edit',
        icon: 'fa-edit',
        iconColor: '#28a745',
        onClick: () => handleOpenProductModal(product)
      },
      {
        text: 'Delete',
        icon: 'fa-trash',
        iconColor: '#dc3545',
        onClick: () => handleDeleteProduct(product._id)
      }
    ];
  };

  return (
    <div>
      <Filter
        appid={appid}
        apps={apps}
        start_date={fromDate}
        end_date={toDate}
        onChangeStartingDate={(e) => setFromDate(e.target.value)}
        onChangeEndDate={(e) => setToDate(e.target.value)}
        onChangeSacco={(e) => {
          setAppid(e);
          getData();
        }}
        showReset={appid?.value}
        handleResetFilters={() => {
          setFromDate(moment().subtract("5", "years").format("YYYY-MM-DD"));
          setToDate(moment().format("YYYY-MM-DD"));
          setAppid(apps?.[0]?.value);
          getData();
        }}
      />

      <div>
        {isLoading ? (
          <Loading icon="fa fa-spinner fa-spin" />
        ) : (
          <>
            <div className="col-md-12">
              <div className="row">
                <CardCount
                  count={summary?.total_products || 0}
                  title="Total Products"
                  icon="fas fa-box"
                  color="primary"
                />
                <CardCount
                  count={summary?.active_products || 0}
                  title="Active Products"
                  icon="fas fa-check-circle"
                  color="success"
                />
                <CardCount
                  count={summary?.inactive_products || 0}
                  title="Inactive Products"
                  icon="fas fa-times-circle"
                  color="danger"
                />
              </div>
            </div>
            <Card className="col-md-12">
              <div className="panel-heading card-title min-height-100">
                <div className="title-section">
                  <i className="fas fa-box title-icon"></i>
                  <span>Loan Products</span>
                </div>
                <div className="card-menus">
                  <button 
                    className="btn btn-compose" 
                    onClick={() => handleOpenProductModal()}
                  >
                    <i className="fa fa-plus mr-1"></i> Create Product
                  </button>
                </div>
              </div>
              
              <div className="panel-body" style={{
                  padding: "15px",
                  overflowX: "auto",
                }}>
                <table className="table table-bordered">
                  <thead>
                    <tr>
                    <th>Name</th>
                      <th>Code</th>
                      <th>Description</th>
                      <th>Amortization Method</th>
                      <th>Interest Rate</th>
                      <th>Duration</th>
                      <th style={{ textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loanProducts.length > 0 ? (
                      loanProducts.map((product, i) => (
                        <tr key={i}>
                          <td>{product?.name || '-'}</td>
                          <td>{product?.code || '-'}</td>
                          <td>{product?.description || '-'}</td>
                          <td>{product?.amortization_method?.toLocaleUpperCase()?.replace('_', ' ') || '-'}</td>
                          <td>{product?.interest_rate || '-'} {product?.interest_frequency ? `  (${product?.interest_frequency})` : ''}</td>
                          <td>{product?.duration || '-'} {product?.duration_unit ? `(${product?.duration_unit})` : ''}</td>
                          <td style={{ textAlign: "center" }}>
                            <DropdownButton 
                              buttonText="Actions"
                              actions={getProductActions(product)}
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} align="center">
                          No Loan Products Available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
                <Pagination
                  totalItemsCount={loanProducts.length}
                  page={1}
                  limit={10}
                  handleChangeLimit={(e) => setLimit(e.target.value)}
                  handlePagination={(page) => setPage(page)}
                />
            </Card>
          </>
        )}
      </div>

      {/* Product Modal */}
      <CreateLoanProductModal
        showProductModal={showProductModal}
        handleCloseModal={handleCloseModal}
        selectedProduct={selectedProduct}
        loanProductData={loanProductData}
        setLoanProductData={setLoanProductData}
        validationErrors={validationErrors}
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
      />
    </div>
  );
};

export default LoanProducts;

